import React from 'react';
import { useUserPermissionAndMenu } from '../contexts/UserPermissionAndMenuProvider';
import NoAccessPage from '../pages/NoAccessPage';
import Loading from '../components/Loading';
import { useTranslation } from 'react-i18next';

interface MenuBasedRouteGuardProps {
    /** The it_system_menu.to_url that grants this route, e.g. "/users". Query strings are ignored. */
    requiredMenuPath: string;
    children: React.ReactNode;
}

/**
 * Guards a route by the user's it_user_menu grants.
 *
 * Shows the page only when the user has an enabled menu row whose to_url matches
 * requiredMenuPath (query string ignored, so "/transaction?setup=X" grants
 * "/transaction"). Falls open when no it_system_menu row exists for the path at
 * all - see canAccessMenuPath. No role-based bypass.
 *
 * A denied result here always means "the route is real but this account has no
 * grant for it", so it renders NoAccessPage rather than the 404.
 */
const MenuBasedRouteGuard: React.FC<MenuBasedRouteGuardProps> = ({ requiredMenuPath, children }) => {
    const { canAccessMenuPath } = useUserPermissionAndMenu();
    const { t } = useTranslation();

    const { allowed, ready } = canAccessMenuPath(requiredMenuPath);

    if (!ready) {
        return <Loading message={t('guard.menu.loading', { defaultValue: 'Loading permissions...' })} />;
    }

    if (!allowed) {
        return <NoAccessPage />;
    }

    return <>{children}</>;
};

export default MenuBasedRouteGuard;
